/* ═══════════════════════════════════════════════════════
   NYC Suite: the sign-in steps that talk to Firebase
   ═══════════════════════════════════════════════════════
   docs/ops/password-signin.md. Everything here goes through src/firebase.js
   (so a test harness can swap that one module). The decisions live in
   src/lib/passwordSignIn.js.

   - signInWithPassword: either signed in, or "code-needed" with the
     resolver for the 6-digit code (Firebase answers
     auth/multi-factor-auth-required when the account has an authenticator).
     There is no Google sign-in any more (2026-09-29); a session Google
     started before then is still judged by checkSession.
   - finishCodeSignIn: the code step.
   - startAuthenticatorSetup / finishAuthenticatorSetup: enrollment.
   - checkSession: what the app does with a signed-in session (decideGate),
     fetching the switch only when it matters: straight from Firestore
     (authPolicy/current, which the nyc-suite rules let any signed-in
     account read), else through the getAuthPolicy callable (before those
     rules are live). A switch that cannot be fetched either way counts as
     "required".
*/
import {
  auth, db, doc, getDocFromServer, callable, signInWithEmailAndPassword, sendPasswordResetEmail,
  getMultiFactorResolver, multiFactor, TotpMultiFactorGenerator,
} from "../firebase.js";
import { ISSUER, decideGate, needsCodeCheck, normalizePolicy } from "./passwordSignIn.js";

const MFA_REQUIRED = "auth/multi-factor-auth-required";

// Firebase's multi-factor error carries what the code step needs.
function codeStep(error) {
  const resolver = getMultiFactorResolver(auth, error);
  const hint = (resolver?.hints ?? []).find((h) => h?.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!hint) return { status: "unsupported-factor" };
  return { status: "code-needed", resolver, hint };
}

export async function signInWithPassword(email, password) {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    return { status: "signed-in" };
  } catch (e) {
    if (e?.code === MFA_REQUIRED) return codeStep(e);
    throw e;
  }
}

export async function finishCodeSignIn(step, code) {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(step.hint.uid, code);
  await step.resolver.resolveSignIn(assertion);
}

// The reset link comes back to this site when the site is on Firebase's list
// of allowed domains; if it is not, the plain link (Firebase's own page) is
// sent instead of failing.
const CONTINUE_URL_ERRORS = new Set(["auth/unauthorized-continue-uri", "auth/invalid-continue-uri", "auth/missing-continue-uri", "auth/unauthorized-domain"]);
// With email enumeration protection on (the project's setting), Firebase
// answers the same for an address with no account; with it off it says
// user-not-found. The screen must not tell the two apart either way.
const NO_ACCOUNT = "auth/user-not-found";
export async function sendPasswordReset(email) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  try {
    if (origin.startsWith("https://") || origin.startsWith("http://localhost")) {
      try {
        await sendPasswordResetEmail(auth, email, { url: origin });
        return;
      } catch (e) {
        if (!CONTINUE_URL_ERRORS.has(e?.code)) throw e;
      }
    }
    await sendPasswordResetEmail(auth, email);
  } catch (e) {
    if (e?.code !== NO_ACCOUNT) throw e;
  }
}

export async function startAuthenticatorSetup(user) {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  // generateQrCodeUrl puts both values into the otpauth:// address as given,
  // so they are encoded here ("NYC Suite" has a space; an email can hold a +).
  const uri = secret.generateQrCodeUrl(encodeURIComponent(user.email || ""), encodeURIComponent(ISSUER));
  return { secret, key: secret.secretKey, uri };
}

export async function finishAuthenticatorSetup(user, secret, code) {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(user).enroll(assertion, "Authenticator app");
}

const factsOf = (token, enrolledCount = 0) => ({
  provider: token.signInProvider,
  role: typeof token.claims?.role === "string" ? token.claims.role : null,
  secondFactor: token.signInSecondFactor ?? null,
  emailVerified: token.claims?.email_verified === true,
  enrolledCount,
});

async function sessionFacts(user) {
  let token = await user.getIdTokenResult();
  // Google without an event role, machines, and sign-ins that passed the
  // code: nothing more to look at.
  if (!needsCodeCheck(factsOf(token)) || token.signInSecondFactor === "totp") return factsOf(token);
  // A password sign-in or an event role without the code: make sure the
  // email status and the list of authenticators are the server's current
  // ones, not a cached copy.
  await user.reload();
  if (token.claims?.email_verified !== true && user.emailVerified === true) token = await user.getIdTokenResult(true);
  return factsOf(token, multiFactor(user).enrolledFactors.length);
}

// The whole switch read (the document, then the function) must answer in
// this long, or the answer is "required" and a late one is ignored. Longer
// than the functions' own 5 s: the function may be starting cold.
export const POLICY_BUDGET_MS = 10_000;

export async function fetchPasswordCodePolicy({ budgetMs = POLICY_BUDGET_MS } = {}) {
  let timer;
  const late = new Promise((resolve) => {
    timer = setTimeout(() => resolve("required"), budgetMs);
  });
  try {
    return await Promise.race([readPasswordCodePolicy(), late]);
  } finally {
    clearTimeout(timer);
  }
}

async function readPasswordCodePolicy() {
  // 1. The document itself, from the server, never a cached copy (a missing
  // document is "required"; no connection falls through to "required").
  try {
    const snap = await getDocFromServer(doc(db, "authPolicy", "current"));
    return normalizePolicy(snap.exists() ? snap.data()?.passwordCode : undefined);
  } catch {
    // Not readable here yet (the rules that allow it are not live): ask the function.
  }
  // 2. The callable, which reads it on the server.
  try {
    const res = await callable("getAuthPolicy")({});
    return normalizePolicy(res?.data?.passwordCode);
  } catch {
    return "required";
  }
}

/**
 * What the app does with this signed-in user: { status: "ok" | "enroll" |
 * "sign-in-again" | "unverified", message? }. Throws when the session itself
 * cannot be read (no connection); the caller shows "try again".
 */
export async function checkSession(user, deps = { sessionFacts, fetchPasswordCodePolicy }) {
  const facts = await deps.sessionFacts(user);
  const first = decideGate(facts);
  if (first.status !== "needs-policy") return first;
  return decideGate(facts, await deps.fetchPasswordCodePolicy());
}
